import { memo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import Calendar from '@/assets/ic_calendar.svg';
import { Button } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import ROUTES from '@/router/routes';
import { cn, formatNumberToLocale } from '@/utils';

const formatTextSize = (maxLength: number, text?: string) => {
  if (typeof text === 'undefined') return undefined;
  return text.length > maxLength ? text.slice(0, maxLength) + '..' : text;
};

const canShowTooltip = (text: string, maxLength: number) => {
  return text && text.length > maxLength;
};

const Position = ({ label, value }: { label: string; value: string }) => {
  const isMobile = useMediaQuery({ query: '(max-width: 425px)' });
  return (
    <div className="flex flex-col gap-y-2 items-center">
      <div className="text-left">
        <p
          className={cn(
            'font-semibold text-gdt-gray-2 text-xs whitespace-nowrap uppercase'
          )}
          id={label.replaceAll(' ', '')}
        >
          {formatTextSize(isMobile ? 15 : 20, label)}
        </p>
        {canShowTooltip(label, isMobile ? 15 : 20) && (
          <Tooltip
            anchorSelect={`#${label.replaceAll(' ', '')}`}
            content={label}
          />
        )}

        <span className={`font-bold text-gdt-gray text-nowrap `}>{value}</span>
      </div>
    </div>
  );
};

export const RNKFecha = memo(function RNKFECHA() {
  const { userInitialData } = useAuth();
  const { isMobile } = useIsDesktop();
  const navigate = useNavigate();
  let texts = {
    title: userInitialData?.primera_fecha ? 'Primera fecha' : 'Última fecha',
    activeDate: userInitialData?.nombre_fecha,
    generalPosition: '-',
    statePosition: '-',
    hoodPosition: '-',
    points: '-',
  };

  if (userInitialData?.fecha_publicada) {
    texts.points = formatNumberToLocale(userInitialData?.puntos_general || 0);
    texts.points = formatNumberToLocale(userInitialData?.puntos_fecha || 0);
    texts.generalPosition = formatNumberToLocale(
      userInitialData?.posicion_fecha || 0
    );
    texts.statePosition = formatNumberToLocale(
      userInitialData?.infoRankingProvincia?.posicionFecha ?? 0
    );
    texts.hoodPosition = formatNumberToLocale(
      userInitialData.rankingLocalidad?.posicionFecha ?? 0
    );
  }

  texts = {
    ...texts,
    points: texts.points === '0' ? '-' : texts.points,
    generalPosition:
      texts.generalPosition === '0' ? '-' : texts.generalPosition,
    statePosition: texts.statePosition === '0' ? '-' : texts.statePosition,
    hoodPosition: texts.hoodPosition === '0' ? '-' : texts.hoodPosition,
  };

  const goToCompleteData = () => {
    navigate(ROUTES.editAccount.myData);
  };

  return (
    <div className="flex flex-col w-full  gap-y-4 h-full">
      <div className="flex gap-x-3 items-center">
        <div className="p-3 bg-gdt-blue-light2 rounded-full">
          <img alt="ic Calendar" className="w-4" src={Calendar} />
        </div>
        <span className="font-bold text-zinc-900 text-lg">{texts.title}</span>
        <span className="font-bold text-gdt-gray-2 text-sm">
          {texts.activeDate}
        </span>
      </div>
      <div className="flex flex-col h-full">
        <div className="py-10 px-4 lg:px-8 bg-white rounded-t-md w-full border-b border-gdt-gray-6 h-fit">
          <div className="flex gap-x-3 items-start justify-between flex-1">
            <div className="flex gap-3 items-start flex-col md:flex-row">
              <span className="text-sm text-gdt-gray-3">MIS PUNTOS</span>
              <span className="text-4xl text-gdt-blue-header">
                {texts.points}
              </span>
            </div>
            {!userInitialData?.primera_fecha && (
              <Button
                className={`font-bold text-sm justify-self-center ${
                  isMobile ? '' : 'mr-6'
                }`}
                type="button"
                variant={isMobile ? 'secondary' : 'text'}
                onClick={() =>
                  navigate(ROUTES.myTeam11, {
                    state: {
                      showScoreTab:
                        userInitialData?.posicion_fecha &&
                        userInitialData?.posicion_fecha > 0,
                    },
                  })
                }
              >
                Ver puntajes
              </Button>
            )}
          </div>
        </div>

        <div className="py-3 bg-white rounded-b-md w-full flex-1">
          <div className="flex flex-col gap-y-5">
            <div className="flex gap-x-3 items-center justify-between">
              <span className="text-sm text-zinc-900 font-bold px-4 lg:px-8">
                Ranking
              </span>
              {!userInitialData?.barrio && !userInitialData?.dtExtranjero && (
                <span className="text-2xs font-semibold text-gdt-gray-3">
                  Para conocer el de tu Provincia y Barrio,{' '}
                  <button
                    className="text-gdt-blue underline"
                    onClick={goToCompleteData}
                  >
                    completá tus datos.
                  </button>
                </span>
              )}
            </div>
            <div className="flex justify-evenly">
              <div className="flex flex-col gap-y-2 pl-4 lg:pl-8 md:border-r md:border-gdt-gray-6 flex-1 lg:max-w-[140px] max-w-[80px]">
                <div className="text-left">
                  <p className="font-semibold text-gdt-gray-2 text-xs">
                    GENERAL
                  </p>
                  <span className="font-bold text-sm text-gdt-gray">
                    {texts.generalPosition}
                  </span>
                </div>
              </div>

              {userInitialData?.dtExtranjero ? (
                <div className="flex flex-col gap-y-2 md:border-r items-center flex-1 lg:min-w-[140px] min-w-[125px]">
                  <Position
                    label={
                      userInitialData.rankingLocalidad?.nombreLocalidad ??
                      'PAIS'
                    }
                    value={texts.hoodPosition}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-y-2  md:border-r md:border-gdt-gray-6 flex-1 items-center lg:min-w-[140px] min-w-[125px]">
                  <Position
                    label={userInitialData?.provincia ?? 'PROVINCIA'}
                    value={texts.statePosition}
                  />
                </div>
              )}
              <div className="flex flex-col gap-y-2 items-center flex-1 lg:min-w-[150px] min-w-[125px]">
                {!userInitialData?.dtExtranjero && (
                  <Position
                    label={userInitialData?.barrio || 'BARRIO'}
                    value={texts.hoodPosition}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
